import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FinishItemView, FinishRule, WorkspaceView } from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  TextArea,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { closeCheck, closeVisit, finishCheck, finishConsultation } from '../api/consultation.ts';
import { queueStep } from '../api/triage.ts';
import { FollowUpButton } from '../appointments/follow-up-button.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { SendToDialog } from '../routes/queue-board.tsx';
import { toasts } from '../toasts.ts';
import styles from './consultation.module.css';

// Finishing the consultation (plan §4.3, §7.1): what is missing is listed inline, each an
// interruptive item acknowledged with a reason; then "Send to" the next queue, or "Finish
// here". Closing the visit is a separate action for whoever ends the last step (P3, P18).

const REASON_MIN = 5;

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

export function FinishPanel({
  view,
  consultationId,
  inProgress,
  noun = 'Consultation',
}: {
  view: WorkspaceView;
  consultationId: string;
  inProgress: boolean;
  /** What is being finished, in words: a consultation or a ward round. */
  noun?: 'Consultation' | 'Ward round';
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [items, setItems] = useState<FinishItemView[] | null>(null);
  const [reasons, setReasons] = useState<Partial<Record<FinishRule, string>>>({});
  const [sending, setSending] = useState(false);
  const setWorkspace = (next: WorkspaceView) => {
    queryClient.setQueryData(['visits', view.visit.id, 'workspace'], next);
  };

  const finish = useMutation({
    mutationFn: (acknowledgements: { ruleId: FinishRule; reason: string }[]) =>
      finishConsultation(consultationId, acknowledgements),
    onSuccess: (next) => {
      setWorkspace(next);
      setItems(null);
      toasts.show({ title: `${noun} finished` });
      if (next.queueEntry !== null) setSending(true);
    },
  });
  const check = useMutation({
    mutationFn: () => finishCheck(consultationId),
    onSuccess: (result) => {
      if (result.items.length === 0) finish.mutate([]);
      else setItems(result.items);
    },
  });
  const finishHere = useMutation({
    mutationFn: (entryId: string) => queueStep(entryId, 'finish'),
    onSuccess: async () => {
      setSending(false);
      await queryClient.invalidateQueries({ queryKey: ['queues'] });
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'workspace'] });
      toasts.show({ title: 'Patient finished here' });
    },
  });

  const allGiven =
    items?.every((i) => (reasons[i.ruleId] ?? '').trim().length >= REASON_MIN) ?? false;
  const submit = () => {
    if (items === null) check.mutate();
    else if (allGiven)
      finish.mutate(
        items.map((i) => ({ ruleId: i.ruleId, reason: (reasons[i.ruleId] ?? '').trim() })),
      );
  };
  useShortcut(
    'mod+shift+enter',
    () => {
      if (inProgress && online && !finish.isPending && !check.isPending) submit();
    },
    { allowInFields: true, description: `Finish ${noun.toLowerCase()}` },
  );
  const error = check.error ?? finish.error;
  const entry = view.queueEntry;

  return (
    <section className={styles.panel} aria-label={`Finish ${noun.toLowerCase()}`}>
      {items === null
        ? null
        : items.map((item) => (
            <AlertPanel
              key={item.ruleId}
              title={item.message}
              reason={reasons[item.ruleId] ?? ''}
              onReasonChange={(value) => {
                setReasons((prev) => ({ ...prev, [item.ruleId]: value }));
              }}
              reasonLabel="Reason for finishing anyway"
              {...((reasons[item.ruleId] ?? '').trim().length > 0 &&
              (reasons[item.ruleId] ?? '').trim().length < REASON_MIN
                ? { reasonError: `Give a reason of at least ${String(REASON_MIN)} characters.` }
                : {})}
            />
          ))}
      {error === null ? null : (
        <Notice tone="error">
          {errorText(error, `The ${noun.toLowerCase()} could not be finished.`)}
        </Notice>
      )}
      {!inProgress ? null : (
        <div className={styles.actions}>
          <Button
            variant="primary"
            isDisabled={!online || (items !== null && !allGiven)}
            isPending={check.isPending || finish.isPending}
            pendingLabel="Finishing…"
            onPress={submit}
          >
            {`Finish ${noun.toLowerCase()} (Ctrl Shift Enter)`}
          </Button>
          <FollowUpButton visitId={view.visit.id} patientId={view.visit.patientId} />
          {items === null ? null : (
            <Button
              variant="quiet"
              onPress={() => {
                setItems(null);
              }}
            >
              Keep working
            </Button>
          )}
        </div>
      )}
      {entry === null ? null : (
        <SendToDialog
          entryId={entry.id}
          fromPointId={entry.servicePointId}
          isOpen={sending}
          onOpenChange={setSending}
          onSent={(name) => {
            setSending(false);
            void queryClient.invalidateQueries({ queryKey: ['queues'] });
            void queryClient.invalidateQueries({
              queryKey: ['visits', view.visit.id, 'workspace'],
            });
            toasts.show({ title: `Patient sent to ${name}` });
          }}
        />
      )}
      {entry !== null && !sending && view.consultations.every((c) => c.status !== 'in_progress') ? (
        <div className={styles.actions}>
          <Button
            onPress={() => {
              setSending(true);
            }}
          >
            Send to
          </Button>
          {entry.status === 'in_service' ? (
            <Button
              variant="quiet"
              isPending={finishHere.isPending}
              pendingLabel="Finishing…"
              onPress={() => {
                finishHere.mutate(entry.id);
              }}
            >
              Finish here
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function CloseVisitDialog({
  view,
  isOpen,
  onOpenChange,
}: {
  view: WorkspaceView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const load = useQuery({
    queryKey: ['visits', view.visit.id, 'close-check'],
    queryFn: () => closeCheck(view.visit.id),
    enabled: isOpen,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const result = isOpen ? (load.data ?? null) : null;
  const close = useMutation({
    mutationFn: () =>
      closeVisit(view.visit.id, result?.needsReason.length === 0 ? undefined : reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id] });
      await queryClient.invalidateQueries({ queryKey: ['patients', view.visit.patientId] });
      onOpenChange(false);
      toasts.show({ title: 'Visit closed' });
    },
  });
  const needsReason = (result?.needsReason.length ?? 0) > 0;
  return (
    <Dialog
      title="Close visit"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) setReason('');
        onOpenChange(open);
      }}
    >
      {load.isFetching && result === null ? (
        <LoadingBlock label="Checking the visit" rows={2} />
      ) : null}
      {load.error === null ? null : (
        <Notice tone="error">{errorText(load.error, 'The visit could not be checked.')}</Notice>
      )}
      {result === null ? null : (
        <>
          {result.blocking.map((b) => (
            <Notice key={b.code} tone="info">
              {b.message}
            </Notice>
          ))}
          {result.blocking.length === 0 && !needsReason ? (
            <p>Everything for this visit is done. Close it now?</p>
          ) : null}
          {result.blocking.length === 0 && needsReason ? (
            <>
              <ul className={styles.list}>
                {result.needsReason.map((n) => (
                  <li key={n.code} className={styles.item}>
                    {n.message}
                  </li>
                ))}
              </ul>
              <TextArea label="Reason for closing" value={reason} onChange={setReason} rows={2} />
            </>
          ) : null}
        </>
      )}
      {close.error === null ? null : (
        <Notice tone="error">{errorText(close.error, 'The visit could not be closed.')}</Notice>
      )}
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={
            result === null ||
            result.blocking.length > 0 ||
            (needsReason && reason.trim().length < REASON_MIN)
          }
          isPending={close.isPending}
          pendingLabel="Closing…"
          onPress={() => {
            close.mutate();
          }}
        >
          Close visit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
